/**
 * YearView V2 — "Garden of days" reimagined.
 *
 * Changes:
 *   • Bigger cells (18px), generous gap so colors breathe.
 *   • Subtle vertical dividers between months for legibility.
 *   • Calendar-year window (Jan 1 → Dec 31 of `year`) — receives whole grid
 *     pre-built by the backend (`days` already covers Jan 1 → Dec 31).
 *   • Native scrollbar hidden; custom premium navigation via left/right
 *     arrow buttons + horizontal mouse drag.
 *   • Enriched floating tooltip with dimensions per day (score-only data
 *     ships in the `days[].dimensions` payload — no extra round trip).
 *   • Selected day gets a glow + ring + scale for instant feedback.
 *   • Year header `◀ 2026 ▶` with available years navigation.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  calendarColor,
  calendarBandLabel,
  parseISODate,
  toISO,
  mondayOf,
  sundayOf,
  addDays,
  formatDateLong,
  MONTHS,
} from "@/lib/calendar";
import { formatDateAR } from "@/lib/dates";
import { DIMENSION_LABELS, formatScore } from "@/lib/scoring";

const CELL = 18; // px (was 14)
const GAP = 4;   // px
const COL_W = CELL + GAP;

function dayCount(a, b) {
  return Math.round((b - a) / 86400000);
}

export default function YearView({
  days,
  selectedDate,
  onSelect,
  year,
  availableYears = [],
  onYearChange,
  todayISO,
}) {
  const containerRef = useRef(null);
  const dragStateRef = useRef({ active: false, startX: 0, startLeft: 0, moved: false });
  const [hover, setHover] = useState(null); // {iso, x, y}

  const { columns, monthBoundaries } = useMemo(() => {
    if (!days?.length) return { columns: [], monthBoundaries: [] };

    const first = parseISODate(days[0].date);
    const last = parseISODate(days[days.length - 1].date);
    const gridStart = mondayOf(first);
    const gridEnd = sundayOf(last);

    const byIso = new Map(days.map((d) => [d.date, d]));
    const total = dayCount(gridStart, gridEnd) + 1;
    const weeks = Math.ceil(total / 7);

    const cols = [];
    for (let w = 0; w < weeks; w++) {
      const col = [];
      for (let r = 0; r < 7; r++) {
        const d = addDays(gridStart, w * 7 + r);
        const iso = toISO(d);
        const inRange = d >= first && d <= last;
        col.push({ iso, date: d, data: inRange ? byIso.get(iso) : null, inRange });
      }
      cols.push(col);
    }

    // Track month boundaries: column index where each month starts (based on
    // the first in-range cell). Used to draw subtle dividers + labels.
    const boundaries = [];
    let prevMonth = -1;
    cols.forEach((col, idx) => {
      const firstInRange = col.find((c) => c.inRange);
      if (!firstInRange) return;
      const m = firstInRange.date.getMonth();
      if (m !== prevMonth) {
        boundaries.push({ colIndex: idx, month: m });
        prevMonth = m;
      }
    });
    return { columns: cols, monthBoundaries: boundaries };
  }, [days]);

  // Auto-scroll to current month when current-year, else to start of year
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el || !columns.length || !todayISO) return;
    const todayDate = parseISODate(todayISO);
    if (todayDate.getFullYear() === year) {
      // Scroll so today is roughly in the middle-right
      const todayCol = columns.findIndex((c) => c.some((x) => x.iso === todayISO));
      if (todayCol >= 0) {
        const target = Math.max(0, todayCol * COL_W - el.clientWidth * 0.6);
        el.scrollLeft = target;
      } else {
        el.scrollLeft = el.scrollWidth;
      }
    } else {
      el.scrollLeft = 0;
    }
  }, [columns.length, year, todayISO]);

  // Keep "today" anchored when the card resizes (e.g. drawer opens)
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    let last = el.clientWidth;
    const ro = new ResizeObserver(() => {
      const next = el.clientWidth;
      if (Math.abs(next - last) > 20) {
        // significant width change → re-anchor to today if in current year
        if (todayISO && parseISODate(todayISO).getFullYear() === year) {
          const todayCol = columns.findIndex((c) => c.some((x) => x.iso === todayISO));
          if (todayCol >= 0) {
            const target = Math.max(0, todayCol * COL_W - el.clientWidth * 0.6);
            el.scrollLeft = target;
          }
        }
        last = next;
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [columns, year, todayISO]);

  // Mouse drag to pan
  const onMouseDown = (e) => {
    const el = containerRef.current;
    if (!el) return;
    dragStateRef.current = {
      active: true,
      startX: e.clientX,
      startLeft: el.scrollLeft,
      moved: false,
    };
  };
  const onMouseMove = (e) => {
    const s = dragStateRef.current;
    if (!s.active) return;
    const el = containerRef.current;
    if (!el) return;
    const dx = e.clientX - s.startX;
    if (Math.abs(dx) > 4) s.moved = true;
    el.scrollLeft = s.startLeft - dx;
  };
  const endDrag = () => {
    dragStateRef.current.active = false;
    // Reset moved flag after the synthetic click finishes
    setTimeout(() => { dragStateRef.current.moved = false; }, 0);
  };

  const scrollBy = (delta) => {
    const el = containerRef.current;
    if (!el) return;
    el.scrollBy({ left: delta, behavior: "smooth" });
  };

  const yearIndex = availableYears.indexOf(year);
  const canPrevYear = yearIndex > 0;
  const canNextYear = yearIndex >= 0 && yearIndex < availableYears.length - 1;

  return (
    <div
      className="relative rounded-3xl p-5 sm:p-7 overflow-hidden backdrop-blur-md border"
      style={{
        background:
          "linear-gradient(155deg, rgba(255, 251, 240, 0.78) 0%, rgba(244, 236, 215, 0.45) 100%)",
        borderColor: "rgba(218, 207, 186, 0.5)",
        boxShadow: "0 18px 50px -28px rgba(83,109,83,0.22), 0 1px 0 rgba(255,255,255,0.65) inset",
      }}
      data-testid="calendar-year-view"
      data-year={year}
    >
      {/* Header: title + year nav + legend */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => canPrevYear && onYearChange(availableYears[yearIndex - 1])}
            disabled={!canPrevYear}
            className="h-9 w-9 rounded-full flex items-center justify-center text-ink-600 hover:text-ink-900 transition border backdrop-blur-md disabled:opacity-30 disabled:cursor-not-allowed"
            style={{
              background: "rgba(255, 251, 240, 0.7)",
              borderColor: "rgba(218, 207, 186, 0.55)",
            }}
            aria-label="Año anterior"
            data-testid="year-prev"
          >
            <ChevronLeft size={16} />
          </button>
          <div className="text-center min-w-[110px]">
            <p className="tk-label">Tu jardín de días</p>
            <p className="font-outfit text-2xl text-ink-900 leading-tight" data-testid="year-title">
              {year}
            </p>
          </div>
          <button
            type="button"
            onClick={() => canNextYear && onYearChange(availableYears[yearIndex + 1])}
            disabled={!canNextYear}
            className="h-9 w-9 rounded-full flex items-center justify-center text-ink-600 hover:text-ink-900 transition border backdrop-blur-md disabled:opacity-30 disabled:cursor-not-allowed"
            style={{
              background: "rgba(255, 251, 240, 0.7)",
              borderColor: "rgba(218, 207, 186, 0.55)",
            }}
            aria-label="Año siguiente"
            data-testid="year-next"
          >
            <ChevronRight size={16} />
          </button>
        </div>
        <div className="flex items-center gap-3">
          <YearLegend />
          <div className="flex items-center gap-1.5">
            <ScrollButton dir="left" onClick={() => scrollBy(-COL_W * 8)} testId="year-scroll-left" />
            <ScrollButton dir="right" onClick={() => scrollBy(COL_W * 8)} testId="year-scroll-right" />
          </div>
        </div>
      </div>

      {/* Grid */}
      <div
        ref={containerRef}
        className="overflow-x-auto pb-2 cursor-grab active:cursor-grabbing tk-no-scrollbar select-none"
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={endDrag}
        onMouseLeave={endDrag}
        data-testid="calendar-year-scroll"
      >
        <div style={{ display: "inline-block", minWidth: "100%" }}>
          {/* Month labels row */}
          <div
            className="relative pl-9"
            style={{ height: 18, marginBottom: 4 }}
            aria-hidden="true"
          >
            {monthBoundaries.map((b) => (
              <span
                key={b.month}
                className="absolute font-outfit text-xs tracking-wide text-ink-600 lowercase"
                style={{ left: 36 + b.colIndex * COL_W, top: 0 }}
                data-testid={`year-month-label-${b.month}`}
              >
                {MONTHS[b.month].toLowerCase()}
              </span>
            ))}
          </div>

          <div className="flex relative">
            {/* Day-of-week labels (Mon, Wed, Fri) */}
            <div
              className="flex flex-col mr-2 select-none"
              aria-hidden="true"
              style={{ gap: GAP }}
            >
              {["L", "", "M", "", "V", "", ""].map((l, i) => (
                <span
                  key={i}
                  className="font-dmsans text-[10px] text-ink-600/70"
                  style={{ height: CELL, lineHeight: `${CELL}px`, width: 14 }}
                >
                  {l}
                </span>
              ))}
            </div>

            {/* Month separators (drawn behind the cells, after the leading row offset) */}
            <div className="absolute top-0 left-9 right-0 bottom-0 pointer-events-none" aria-hidden="true">
              {monthBoundaries.slice(1).map((b) => (
                <span
                  key={`sep-${b.month}`}
                  className="absolute top-0 bottom-0"
                  style={{
                    left: b.colIndex * COL_W - GAP / 2,
                    width: 1,
                    background: "linear-gradient(180deg, rgba(83,109,83,0) 0%, rgba(83,109,83,0.18) 30%, rgba(83,109,83,0.18) 70%, rgba(83,109,83,0) 100%)",
                  }}
                  data-testid="year-month-sep"
                />
              ))}
            </div>

            {/* Columns */}
            <div className="flex">
              {columns.map((col, ci) => (
                <div key={ci} className="flex flex-col" style={{ gap: GAP, marginRight: GAP }}>
                  {col.map((cell) => {
                    if (!cell.inRange) {
                      return (
                        <span
                          key={cell.iso}
                          aria-hidden="true"
                          style={{ width: CELL, height: CELL }}
                        />
                      );
                    }
                    const isSelected = selectedDate === cell.iso;
                    const isToday = todayISO === cell.iso;
                    const data = cell.data || {};
                    const isFuture = data.future === true;
                    const color = isFuture ? "rgba(196, 178, 144, 0.10)" : calendarColor(data.overall);
                    return (
                      <button
                        key={cell.iso}
                        type="button"
                        onClick={() => {
                          if (dragStateRef.current.moved) return;
                          onSelect(cell.iso);
                        }}
                        onMouseEnter={(e) => {
                          if (isFuture) return;
                          const rect = e.currentTarget.getBoundingClientRect();
                          setHover({
                            iso: cell.iso,
                            overall: data.overall ?? null,
                            dimensions: data.dimensions || null,
                            has_reflection: !!data.has_reflection,
                            x: rect.left + rect.width / 2,
                            y: rect.top,
                          });
                        }}
                        onMouseLeave={() => setHover(null)}
                        className={`relative rounded-[6px] transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-sage-400/60 ${
                          isSelected ? "scale-150" : "hover:scale-[1.25]"
                        }`}
                        style={{
                          width: CELL,
                          height: CELL,
                          background: color,
                          border: isToday && !isSelected ? "1px solid rgba(42,157,143,0.55)" : "none",
                          boxShadow: isSelected
                            ? "0 0 0 2px #2A9D8F, 0 0 18px 4px rgba(42,157,143,0.45), 0 4px 12px rgba(83,109,83,0.25)"
                            : isFuture
                            ? "inset 0 0 0 1px rgba(180,165,140,0.18)"
                            : data.overall != null
                            ? "0 1px 3px rgba(83,109,83,0.18)"
                            : "inset 0 0 0 1px rgba(180,165,140,0.25)",
                          zIndex: isSelected ? 5 : 1,
                        }}
                        data-testid={`year-cell-${cell.iso}`}
                        data-has-score={data.overall != null ? "true" : "false"}
                        aria-label={`${formatDateLong(cell.iso)} ${data.overall != null ? data.overall.toFixed(1) : "sin registro"}`}
                      >
                        {data.has_reflection && (
                          <span
                            aria-hidden="true"
                            className="absolute rounded-full bg-white/85"
                            style={{ width: 4, height: 4, top: 2, right: 2 }}
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Floating tooltip */}
      {hover && <HoverTooltip hover={hover} />}
    </div>
  );
}

function ScrollButton({ dir, onClick, testId }) {
  const Icon = dir === "left" ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      onClick={onClick}
      className="h-8 w-8 rounded-full flex items-center justify-center text-ink-600 hover:text-ink-900 transition border backdrop-blur-md"
      style={{
        background: "rgba(255, 251, 240, 0.7)",
        borderColor: "rgba(218, 207, 186, 0.55)",
      }}
      aria-label={dir === "left" ? "Desplazar a la izquierda" : "Desplazar a la derecha"}
      data-testid={testId}
    >
      <Icon size={14} />
    </button>
  );
}

function HoverTooltip({ hover }) {
  // Position the tooltip in a fixed layer just above the cell, centered.
  // We avoid using the parent container — viewport coords give the best fit.
  const width = 220;
  const left = Math.max(12, Math.min(window.innerWidth - width - 12, hover.x - width / 2));
  const top = hover.y - 12;

  return (
    <div
      role="tooltip"
      className="fixed z-50 pointer-events-none rounded-2xl px-4 py-3 backdrop-blur-md border animate-fade-up"
      style={{
        left,
        top,
        width,
        transform: "translateY(-100%)",
        background: "rgba(255, 251, 240, 0.92)",
        borderColor: "rgba(218, 207, 186, 0.6)",
        boxShadow: "0 18px 50px -20px rgba(83,109,83,0.35), 0 1px 0 rgba(255,255,255,0.7) inset",
      }}
      data-testid="year-hover-tooltip"
    >
      <p className="font-outfit text-sm text-ink-900">{formatDateAR(hover.iso)}</p>
      {hover.overall != null ? (
        <>
          <div className="flex items-baseline gap-1.5 mt-1">
            <span className="font-outfit text-2xl text-ink-900">{hover.overall.toFixed(1)}</span>
            <span className="font-dmsans text-[10px] text-ink-600">/ 10</span>
            <span className="font-dmsans text-[10px] text-sage-700 ml-1">
              {calendarBandLabel(hover.overall)}
            </span>
          </div>
          {hover.dimensions && (
            <ul className="mt-2 grid grid-cols-1 gap-1">
              {["sleep", "physical", "mood", "thoughts", "learning"].map((d) => {
                const v = hover.dimensions[d];
                if (v == null) return null;
                return (
                  <li key={d} className="flex items-center justify-between gap-2">
                    <span className="font-dmsans text-[11px] text-ink-600">
                      {DIMENSION_LABELS[d]}
                    </span>
                    <span className="font-outfit text-[11px] text-ink-900">{formatScore(v)}</span>
                  </li>
                );
              })}
            </ul>
          )}
          {hover.has_reflection && (
            <p className="font-dmsans text-[10px] text-sage-700 mt-2">
              Con reflexión escrita
            </p>
          )}
        </>
      ) : (
        <p className="font-dmsans text-xs text-ink-600 mt-1">Sin registro este día.</p>
      )}
    </div>
  );
}

function YearLegend() {
  const stops = [null, 1.5, 3, 5, 7, 9];
  return (
    <div className="flex items-center gap-2" aria-hidden="true">
      <span className="font-dmsans text-[10px] text-ink-600/80">Menos</span>
      <div className="flex items-center gap-1">
        {stops.map((s, i) => (
          <span
            key={i}
            className="rounded-[3px]"
            style={{
              width: 12,
              height: 12,
              background: calendarColor(s),
              boxShadow:
                s == null
                  ? "inset 0 0 0 1px rgba(180,165,140,0.25)"
                  : "0 1px 2px rgba(83,109,83,0.18)",
            }}
          />
        ))}
      </div>
      <span className="font-dmsans text-[10px] text-ink-600/80">Más</span>
    </div>
  );
}
